import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { InsuranceProviderResponse } from '../../@types/Insurance';
import { inquiry } from '../../actions/insurance';
import { prices } from '../../helpers';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import type { RootState } from '../../reducers';
import { getInsuranceList } from '../../selectors/insurance';
import { getItem } from '../../selectors/item';
import { getRetailerId } from '../../selectors/retailer';
import { Column, Columns } from '../../styled-components/components/Columns';
import { Text } from '../../styled-components/components/Text';

const InsuranceHeader = () => {
  const { t } = useTranslation(['item']);
  const dispatch = useDispatch();
  const requestStatus = useSelector<RootState, number>((s) => s.insurances.requestStatus);
  const responseStatus = useSelector<RootState, number>((s) => s.insurances.responseStatus);
  const insurances = useSelector(getInsuranceList);
  const personalNumber = useSelector<RootState, string | undefined>(
    (s) => s.insurances.persisted.personalNumber,
  );
  const drivingRange = useSelector<RootState, string | undefined>(
    (s) => s.insurances.persisted.drivingRange,
  );
  const item = useSelector(getItem);
  const branchId = useSelector(getRetailerId);
  const provider = useSelector<RootState, InsuranceProviderResponse | null>(
    (s) => s.insurances.provider,
  );
  const actions = bindActionCreators({ inquiry }, dispatch);

  const { successful } = useDataExchangeStatus(ExchangeTypes.Fetching, [
    requestStatus,
    responseStatus,
  ]);
  useEffect(() => {
    if (personalNumber && provider && branchId && item) {
      actions.inquiry(item.id, branchId, personalNumber, drivingRange || '1');
    }
  }, [provider]);

  if (successful && (insurances || []).length > 0) {
    const { price, name, unit } = insurances.sort((a, b) => a.price - b.price)[0];

    return (
      <Columns $spacing={2} $valign="center">
        <Column $grow>
          <Text $weight="medium" $size="smallStatic">
            {name}
          </Text>
        </Column>
        <Column $textAlign="right" $noShrink>
          <Text $weight="bold" $size="large">
            {price > 0 ? prices.prettify(price, unit) : `0 ${unit}`}
          </Text>
        </Column>
      </Columns>
    );
  }

  if (provider?.requiresDistance && provider.requiresPersonalNumber)
    return (
      <Text $weight="medium" $size="smallStatic">
        {t('insurance.heading', { ns: 'item' })}
      </Text>
    );

  return (
    <Text $weight="medium" $size="smallStatic">
      {provider?.insuranceHeader || t('insurance.heading', { ns: 'item' })}
    </Text>
  );
};

export default InsuranceHeader;
